<script setup lang="ts">
import type { Ref } from 'vue';
import type { TableFoldProps as JobsAirRoadTableFoldProps } from '@/components/tableFolds/JobsAirRoadTableFold.vue';
import type { HeadersData } from '@/composables/useTable';

import { computed, ref } from 'vue';

import TableFoldBtn from '@/components/btns/TableFoldBtn.vue';
import KRadioField from '@/components/fields/KRadioField.vue';
import TableLoader from '@/components/loaders/TableLoader.vue';
import SortThTableCell from '@/components/tableCells/SortThTableCell.vue';
import JobsAirRoadTableFold from '@/components/tableFolds/JobsAirRoadTableFold.vue';
import TableEmptyText from '@/components/texts/TableEmptyText.vue';
import JobIdAndIconLinkUnit from '@/components/units/JobIdAndIconLinkUnit.vue';
import useTable from '@/composables/useTable';

import tableStyles from '@/styles/table.module.scss';

export interface DataItem {
  id: number;
  jobId: number;
  jobDate: string;
  customerCode: string;
  stop1: string;
  stop2: string;
  stop3: string;
  reference1: string;
  reference2: string;
  reference3: string;
  fold: JobsAirRoadTableFoldProps;
}

export interface Props {
  loading?: boolean;
  data?: DataItem[];
}
const props = withDefaults(defineProps<Props>(), {
  loading: false,
  data: () => [],
});

const model = defineModel<number>({
  default: NaN,
});

const openFoldKey = ref(NaN);

const useTableValues = useTable(props);
const { actives, ascs, handleSort } = useTableValues;
const sortedData = useTableValues.sortedData as Ref<DataItem[]>;

const headersData = computed<HeadersData>(function () {
  // if (props.isStaff) { // use computed to keep the pattern from other tables
  return [
    {
      value: 'jobId',
      title: 'Job ID',
      colspan: 2,
    },
    {
      value: 'jobDate',
      title: 'Date',
    },
    {
      value: 'customerCode',
      title: 'Customer',
    },
    {
      value: 'stop1',
      title: 'Stop 1',
    },
    {
      value: 'stop2',
      title: 'Stop 2',
    },
    {
      value: 'stop3',
      title: 'Stop 3',
    },
    {
      value: 'reference1',
      title: 'Reference 1',
    },
    {
      value: 'reference2',
      title: 'Reference 2',
    },
    {
      value: 'reference3',
      title: 'Reference 3',
    },
  ];
});

const foldColspan = computed(() => {
  return headersData.value.length + 1;
});

function handleClick(dataItemId: number): void {
  if (openFoldKey.value === dataItemId) {
    openFoldKey.value = NaN;
  } else {
    openFoldKey.value = dataItemId;
  }
}
</script>

<template>
  <table
    :class="[
      tableStyles.table,
      tableStyles.folded,
      tableStyles.hovered,
      tableStyles.bordered,
      $style.jobsAirRoadTable,
    ]"
    data-testid="jobsAirRoadTable"
  >
    <thead>
      <tr>
        <template
          v-for="(headerItem, headerIndex) in headersData"
          :key="headerIndex"
        >
          <SortThTableCell
            :colspan="headerItem.colspan"
            :actives="actives"
            :ascs="ascs"
            :handle-sort="handleSort"
            :header-item-value="headerItem.value"
            :is-not-sortable="headerItem.isNotSortable"
            :class="$style[headerItem.value + 'Th']"
          >
            {{ headerItem.title }}
          </SortThTableCell>
        </template>
      </tr>
    </thead>
    <tr v-if="props.loading">
      <td :colspan="foldColspan" class="!h-64 p-0">
        <TableLoader />
      </td>
    </tr>
    <tr v-else-if="!sortedData?.length">
      <td :colspan="foldColspan" class="border-b-0 p-0">
        <TableEmptyText />
      </td>
    </tr>
    <tbody v-else>
      <template v-for="dataItem in sortedData" :key="dataItem.id">
        <tr
          :class="[
            tableStyles.foldTitle,
            { [tableStyles.foldOpen]: openFoldKey === dataItem.id },
          ]"
        >
          <td :class="$style.radioFoldTd">
            <v-radio-group
              v-model="model"
              :class="$style.radioGroup"
              hide-details
            >
              <KRadioField hide-details :value="dataItem.id" size="sm" />
            </v-radio-group>

            <TableFoldBtn
              :class="tableStyles.foldBtn"
              :is-open="openFoldKey === dataItem.id"
              @click="handleClick(dataItem.id)"
            />
          </td>
          <td :class="$style.jobIdTd">
            <JobIdAndIconLinkUnit
              :job-id="dataItem.jobId"
              :job-date="dataItem.jobDate"
            />
          </td>
          <td :class="$style.jobDateTd">
            {{ dataItem.jobDate }}
          </td>
          <td :class="$style.customerCodeTd">
            {{ dataItem.customerCode }}
          </td>
          <td :class="$style.stop1Td">
            {{ dataItem.stop1 }}
          </td>
          <td :class="$style.stop2Td">
            {{ dataItem.stop2 }}
          </td>
          <td :class="$style.stop3Td">
            {{ dataItem.stop3 }}
          </td>
          <td :class="$style.reference1Td">
            {{ dataItem.reference1 }}
          </td>
          <td :class="$style.reference2Td">
            {{ dataItem.reference2 }}
          </td>
          <td :class="$style.reference3Td">
            {{ dataItem.reference3 }}
          </td>
        </tr>
        <tr
          :class="[
            tableStyles.foldContent,
            { [tableStyles.foldOpen]: openFoldKey === dataItem.id },
          ]"
        >
          <td :colspan="foldColspan">
            <div v-if="openFoldKey === dataItem.id">
              <JobsAirRoadTableFold v-bind="dataItem.fold" />
            </div>
          </td>
        </tr>
      </template>
    </tbody>
  </table>
</template>

<!-- prettier-ignore -->
<style module lang="scss">
@use '@/styles/colors';
@use '@/styles/variables';

.jobsAirRoadTable {
  width: 100%;

  & > thead > tr {
    & > th {
      white-space: nowrap;
    }
    & > th {
      position: sticky;
      top: variables.$appBarHeight;
      z-index: 1;
    }
  }
}
  .jobIdTh {
  }
  .jobDateTh {
    min-width: 100px;
    width: 100px;
  }
  .customerCodeTh {
  }
  .stop1Th {
  }
  .stop2Th {
  }
  .stop3Th {
  }
  .reference1Th {
  }
  .reference2Th {
  }
  .reference3Th {
  }
  .radioFoldTd {
    min-width: 67px !important;
    width: 67px !important;
  }
  .jobIdTd {
    min-width: 90px;
    width: 90px;
  }
  .jobDateTd {
    white-space: nowrap;
  }
  .customerCodeTd {
  }
  .stop1Td {
  }
  .stop2Td {
  }
  .stop3Td {
  }
  .reference1Td {
  }
  .reference2Td {
  }
  .reference3Td {
  }

    .radioGroup {
      position: absolute;
      top: calc(50% - 36px / 2);
      right: calc(67px - 32.5px);
    }
</style>
